import { Voice } from './globals'

function groupDigitsSmart(digits: string, minDigitsToGroup = 5): string {
  const n = digits.length;
  if (n < minDigitsToGroup) return digits;

  const chunks: string[] = [];
  let i = 0;

  // If remainder would be 1, we end with ... 2 2
  const remainder = n % 3;
  const tailPairs = remainder === 1 ? 2 : 0; // number of trailing "2" groups

  const headLen = remainder === 1 ? n - 4 : (remainder === 2 ? n - 2 : n);

  // Head in pure 3s
  while (i < headLen) {
    chunks.push(digits.slice(i, i + 3));
    i += 3;
  }

  // Tail: either nothing, one "2", or two "2"s
  if (remainder === 2) {
    chunks.push(digits.slice(i, i + 2));
    i += 2;
  } else if (remainder === 1) {
    chunks.push(digits.slice(i, i + 2));
    i += 2;
    chunks.push(digits.slice(i, i + 2));
    i += 2;
  }

  // Safety (shouldn’t happen)
  if (i < n) chunks.push(digits.slice(i));

  // Join with spaces
  return chunks.join(' ');
}

type TTSFilterOptions = {
  removeBareUrls?: boolean // drop http(s)://... entirely
  bareUrlPlaceholder?: string // if not removing, replace with this (e.g., "länk")
  minDigitsToGroup?: number // only group if sequence length >= this (default 5)
}

export function filterMessageForTTS(
  input: string,
  opts: TTSFilterOptions = {}
): string {
  const {
    removeBareUrls = true,
    bareUrlPlaceholder = '',
    minDigitsToGroup = 5
  } = opts

  let text = input ?? ''

  // 1) Normalize line breaks like <br> to brief spoken pause
  text = text.replace(/<br\s*\/?>/gi, '. ')

  // 2) Strip common Markdown markers (headings, bold/italic, code, HR, blockquotes)
  // Headings at start of line: ### Title  -> Title
  text = text.replace(/^\s{0,3}#{1,6}\s+/gm, '')
  // Bold/italic markers: **text** *text* __text__ _text_ -> text
  text = text.replace(/(\*\*|__)(.*?)\1/g, '$2')
  text = text.replace(/(\*|_)(.*?)\1/g, '$2')
  // Inline code `code` -> code
  text = text.replace(/`([^`]+)`/g, '$1')
  // Blockquotes
  text = text.replace(/^\s{0,3}>\s?/gm, '')
  // Horizontal rules
  text = text.replace(/^\s{0,3}([-*_])(?:\s*\1){2,}\s*$/gm, '')

  // 3) Markdown links: [text](url) -> text  (drop URL)
  text = text.replace(/\[([^\]]+)\]\((?:[^)]+)\)/g, '$1')

  // 4) Image syntax: ![alt](url) -> alt (or drop if no alt)
  text = text.replace(
    /!\[([^\]]*)\]\((?:[^)]+)\)/g,
    (_m, alt: string) => alt || ''
  )

  // 5) Strip simple HTML anchors: <a href="...">label</a> -> label
  text = text.replace(/<a\b[^>]*>(.*?)<\/a>/gis, '$1')

  // 6) Bare URLs: http(s)://...  (either remove or replace with placeholder)
  text = text.replace(
    /\bhttps?:\/\/[^\s)>\]}]+/gi,
    removeBareUrls ? '' : bareUrlPlaceholder
  )

  // 7) Group long digit sequences (no single-digit trailing group)
  //    Apply only to runs of digits length >= minDigitsToGroup
  text = text.replace(/\d{5,}/g, (digits: string) =>
    groupDigitsSmart(digits, minDigitsToGroup)
  )

  // 8) Collapse repeated spaces and tidy punctuation spacing
  text = text
    .replace(/\s{2,}/g, ' ')
    .replace(/\s*([.,;:!?])\s*/g, '$1 ')
    .replace(/\s+\)/g, ')')
    .replace(/\(\s+/g, '(')
    .replace(/\s+$/g, '')
    .trim()

  return text
}

export const generateAudioUrl = (
  message: any,
  locale: string,
  voices?: Voice[]
) => {
  const readSpeaker = {
    api: 'https://tts.readspeaker.com/a/speak',
    token: '418ca64ee6f2a41e542b05fcbc427208',
    lang: {
      sv_SE: 'sv_se',
      nb_NO: 'no_nb',
      en_US: 'en_us'
    },
    voice: {
      sv_SE: voices?.find((x) => x.locale === 'sv_SE')?.voice ?? 'Maja-DNN',
      nb_NO: voices?.find((x) => x.locale === 'nb_NO')?.voice ?? 'Lykke-DNN',
      en_US: voices?.find((x) => x.locale === 'en_US')?.voice ?? 'Mark-DNN'
    }
  }
  const api_key = readSpeaker.token
  const { api } = readSpeaker
  const voice = (readSpeaker.voice as any)[locale]
  const lang = (readSpeaker.lang as any)[locale]

  const filtered = filterMessageForTTS(message, {
    removeBareUrls: true, // drop raw URLs entirely
    bareUrlPlaceholder: '', // or set to 'länk' if you want it spoken
    minDigitsToGroup: 5 // start grouping for 5+ digits
  })

  const uriEncodedMessage = encodeURIComponent(filtered)
  return `${api}?key=${api_key}&text=${uriEncodedMessage}&lang=${lang}&voice=${voice}`
}

export const generateWaitMessage = (
  locale: string,
  voices?: Voice[],
  waitIndex?: number
) => {
  const waitPhrasesObj: any = {
    sv_SE: voices?.find((x) => x.locale === 'sv_SE')?.waitPhrases ?? [
      'Ett ögonblick',
      'Vargod vänta'
    ],
    nb_NO: voices?.find((x) => x.locale === 'nb_NO')?.waitPhrases ?? [
      'Ett ögonblick',
      'Vargod vänta'
    ],
    en_US: voices?.find((x) => x.locale === 'en_US')?.waitPhrases ?? [
      'A moment please',
      'Please wait'
    ]
  }
  const waitPhrases: string[] | undefined = waitPhrasesObj[locale]

  if (!waitPhrases?.length) {
    return null
  }

  const waitMessage = waitPhrases[(waitIndex || 0) % waitPhrases.length]

  return generateAudioUrl(waitMessage, locale, voices)
}

export const generateStartTranscribe = (
  locale: string,
  voices?: Voice[],
  waitIndex?: number
) => {
  const startTranscribeObj: any = {
    sv_SE: voices?.find((x) => x.locale === 'sv_SE')
      ?.startTranscribePhrases ?? [
      'Vänta lite, transkriberar',
      'Processar info',
      'Transkriberar'
    ],
    nb_NO: voices?.find((x) => x.locale === 'nb_NO')
      ?.startTranscribePhrases ?? ['Ett ögonblick', 'Vargod vänta'],
    en_US: voices?.find((x) => x.locale === 'en_US')
      ?.startTranscribePhrases ?? ['A moment please', 'Please wait']
  }
  const startPhrases: string[] | undefined = startTranscribeObj[locale]

  if (!startPhrases?.length) {
    return null
  }

  const startPhrase = startPhrases[(waitIndex || 0) % startPhrases.length]

  return generateAudioUrl(startPhrase, locale, voices)
}

export const generateStopTranscribe = (
  locale: string,
  voices?: Voice[],
  waitIndex?: number
) => {
  return null

  // return generateWaitMessage(locale,voices,waitIndex)
  // const stopTranscribeObj: any = {
  //   sv_SE: voices?.find((x) => x.locale === 'sv_SE')
  //     ?.stopTranscribePhrases ?? ['Ok'],
  //   nb_NO: voices?.find((x) => x.locale === 'nb_NO')
  //     ?.stopTranscribePhrases ?? ['Ok'],
  //   en_US: voices?.find((x) => x.locale === 'en_US')
  //     ?.stopTranscribePhrases ?? ['Ok']
  // }
  // const stopPhrases: string[] | undefined = stopTranscribeObj[locale]

  // if (!stopPhrases?.length) {
  //   return null
  // }

  // const stopPhrase = stopPhrases[(waitIndex || 0) % stopPhrases.length]

  // return generateAudioUrl(stopPhrase, locale, voices)
}
